import React from "react";
import { WarningAmber } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import { useTheme } from "@mui/material/styles";
import {
  closeIconSize,
  closeIconSx,
  getDescriptionStyle,
  getTitleStyle,
  getToastAlertSx,
  toastIconStyle,
} from "@components/ToastMessage/ToastMessage.styles.tsx";
import type { ToastMessageProps } from "@components/ToastMessage/ToastMessage.types.ts";
import { SIDE_CHILD_DRAWER_Z_INDEX } from "../../constants/layout.style";

const ToastMessage: React.FC<ToastMessageProps> = ({
  open,
  onClose,
  title,
  description,
  autoHideDuration = 3000,
  type,
}) => {
  const theme = useTheme();

  const setIcon = (type: string | undefined) => {
    switch (type) {
      case "success":
        return <CheckCircleIcon data-testid="toast-icon" color="success" />;

      case "error":
        return <CancelIcon data-testid="toast-icon" color="error" />;

      case "warning":
        return <WarningAmber data-testid="toast-icon" color="warning" />;

      default:
        return undefined;
    }
  };
  const icon = setIcon(type);
  return (
    <Snackbar
      data-testid="toast"
      sx={{
        zIndex: SIDE_CHILD_DRAWER_Z_INDEX + 1,
        position: "static",
        left: "auto",
        right: "auto",
        transform: "none",
        top: "auto",
        maxWidth: "100%",
      }}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      autoHideDuration={autoHideDuration}
    >
      <Alert
        icon={<div style={toastIconStyle}>{icon ?? undefined}</div>}
        sx={getToastAlertSx(theme)}
        className="w-full max-w-[612px] mx-auto px-6 py-4 rounded"
      >
        <div className="w-full">
          <div className="flex justify-between items-start">
            <div className="font-bold text-base" style={getTitleStyle(theme)}>
              {title}
            </div>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={onClose}
              className="ml-2 self-start p-0"
              sx={closeIconSx}
              data-testid="toast-close-button"
            >
              <CloseIcon style={closeIconSize} />
            </IconButton>
          </div>

          {description && (
            <div
              data-testid="toast-description"
              className="text-sm mt-2 font-normal whitespace-pre-line"
              style={getDescriptionStyle(theme)}
            >
              {description}
            </div>
          )}
        </div>
      </Alert>
    </Snackbar>
  );
};

export default ToastMessage;
